import { useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Download, LayoutGrid, List, Pencil, Plus, Search, Trash2, Upload, X } from "lucide-react";

import { AdminLoginCard } from "@/components/admin/AdminLoginCard";
import { SelmaAdminMobileNav, SelmaAdminSidebar } from "@/components/admin/SelmaAdminSidebar";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { adminFetch, useAdminSession } from "@/hooks/use-admin-session";

// Selma admin paneli — "Rehber İçeriği" sayfası (psikosomatik_hastaliklar tablosu).
// AdminSelmaDuygusalSebep.tsx'in UI iskeletini referans alır (arama, sıralama,
// grid/list toggle, staleTime:0, invalidateQueries exact match). Mobil uygulamanın ilk
// açılış sayfası olduğu için sidebar'da en üstte. Bkz. CLAUDE.md → "Selma Admin Paneli".
// Bu sayfa İÇERİK ÜRETMEZ — sadece form + liste + JSON içe/dışa aktarma altyapısıdır.

const BOLGE_KATEGORILERI = [
  { value: "tum_beden", label: "Tüm Beden" },
  { value: "bas_boyun", label: "Baş & Boyun" },
  { value: "mide_sindirim", label: "Mide & Sindirim" },
  { value: "sirt_eklemler", label: "Sırt & Eklemler" },
] as const;
type BolgeKategori = (typeof BOLGE_KATEGORILERI)[number]["value"];

function bolgeLabel(v: string): string {
  return BOLGE_KATEGORILERI.find((b) => b.value === v)?.label ?? v;
}

type RehberItem = {
  id: string;
  fiziksel_belirti: string;
  bolge_kategori: BolgeKategori;
  duygusal_neden: string;
  olumlama: string;
  ikon: string | null;
  iliskili_meditasyon_id: string | null;
  created_at: string;
  updated_at: string;
};

const EMPTY_FORM = {
  fiziksel_belirti: "",
  bolge_kategori: "tum_beden" as BolgeKategori,
  duygusal_neden: "",
  olumlama: "",
  ikon: "",
  iliskili_meditasyon_id: "",
};
type FormState = typeof EMPTY_FORM;

async function fetchItems(): Promise<RehberItem[]> {
  const res = await adminFetch("/api/admin/selma/rehber");
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) throw new Error(json?.error ?? "Liste alınamadı.");
  return Array.isArray(json?.data) ? json.data : [];
}

async function createItem(payload: Record<string, unknown>): Promise<void> {
  const res = await adminFetch("/api/admin/selma/rehber", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) throw new Error(json?.error ?? "Kaydedilemedi.");
}

async function updateItem(payload: Record<string, unknown> & { id: string }): Promise<void> {
  const res = await adminFetch(`/api/admin/selma/rehber/${encodeURIComponent(payload.id)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) throw new Error(json?.error ?? "Güncellenemedi.");
}

async function deleteItem(id: string): Promise<void> {
  const res = await adminFetch(`/api/admin/selma/rehber/${encodeURIComponent(id)}`, { method: "DELETE" });
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) throw new Error(json?.error ?? "Silinemedi.");
}

type ImportResult = { insertedCount: number; skippedCount: number; skipped: Array<{ index: number; fiziksel_belirti?: string; reason: string }> };

async function importItems(rows: unknown[]): Promise<ImportResult> {
  const res = await adminFetch("/api/admin/selma/rehber/import", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(rows),
  });
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) throw new Error(json?.error ?? "İçe aktarma başarısız.");
  return json as ImportResult;
}

type MeditasyonOption = { id: string; baslik: string };

async function fetchMeditasyonOptions(): Promise<MeditasyonOption[]> {
  const res = await adminFetch("/api/admin/selma/duygusal-sebep");
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) throw new Error(json?.error ?? "Meditasyon listesi alınamadı.");
  return Array.isArray(json?.data) ? json.data : [];
}

function downloadJson(filename: string, data: unknown) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

const ORNEK_FORMAT = [
  {
    fiziksel_belirti: "Örnek Hastalık İsmi",
    bolge_kategori: "bas_boyun",
    duygusal_neden: "Buraya açıklama/duygusal kök neden metni gelecek.",
    olumlama: "Buraya olumlama cümlesi gelecek.",
    ikon: "psychology",
  },
  {
    fiziksel_belirti: "İkinci Örnek",
    bolge_kategori: "mide_sindirim",
    duygusal_neden: "...",
    olumlama: "...",
    ikon: "local_fire_department",
  },
];

export default function AdminSelmaRehber() {
  const queryClient = useQueryClient();
  const { sessionQuery, loginMutation, logoutMutation } = useAdminSession();
  const configured = Boolean(sessionQuery.data?.configured);
  const authenticated = Boolean(sessionQuery.data?.authenticated);

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [editingItem, setEditingItem] = useState<RehberItem | null>(null);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const importFileRef = useRef<HTMLInputElement>(null);

  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<"newest" | "title-asc">("newest");
  const [viewMode, setViewMode] = useState<"grid" | "list">("list");
  const [activeBolge, setActiveBolge] = useState<BolgeKategori | "tumu">("tumu");

  const listQuery = useQuery({
    queryKey: ["selma-rehber"],
    queryFn: fetchItems,
    enabled: authenticated,
    staleTime: 0,
  });

  const meditasyonQuery = useQuery({
    queryKey: ["selma-rehber-meditasyon-options"],
    queryFn: fetchMeditasyonOptions,
    enabled: authenticated,
    staleTime: 0,
  });
  const meditasyonOptions = meditasyonQuery.data ?? [];

  const createMutation = useMutation({
    mutationFn: createItem,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["selma-rehber"], exact: true });
      setShowForm(false);
      setForm(EMPTY_FORM);
    },
  });

  const updateMutation = useMutation({
    mutationFn: updateItem,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["selma-rehber"], exact: true });
      setEditingItem(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteItem,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["selma-rehber"], exact: true });
    },
  });

  const importMutation = useMutation({
    mutationFn: importItems,
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ["selma-rehber"], exact: true });
      setImportResult(result);
      setImportError(null);
    },
    onError: (err: Error) => {
      setImportError(err.message);
      setImportResult(null);
    },
  });

  async function handleExport() {
    try {
      const res = await adminFetch("/api/admin/selma/rehber/export");
      if (!res.ok) {
        const json = await res.json().catch(() => null);
        throw new Error(json?.error ?? "Dışa aktarma başarısız.");
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const dateStr = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `rehber-icerik-${dateStr}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      alert(err.message ?? "Dışa aktarma başarısız.");
    }
  }

  async function handleImportFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      if (!Array.isArray(parsed)) throw new Error("Dosya bir JSON array içermeli.");
      importMutation.mutate(parsed);
    } catch (err: any) {
      setImportError(err.message ?? "Dosya okunamadı/parse edilemedi.");
      setImportResult(null);
    } finally {
      e.target.value = "";
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (createMutation.isPending) return;
    if (!form.fiziksel_belirti.trim() || !form.duygusal_neden.trim() || !form.olumlama.trim()) return;
    createMutation.mutate({
      fiziksel_belirti: form.fiziksel_belirti.trim(),
      bolge_kategori: form.bolge_kategori,
      duygusal_neden: form.duygusal_neden.trim(),
      olumlama: form.olumlama.trim(),
      ikon: form.ikon.trim() || null,
      iliskili_meditasyon_id: form.iliskili_meditasyon_id || null,
    });
  }

  const items = listQuery.data ?? [];

  const visibleItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    let rows = items;
    if (activeBolge !== "tumu") rows = rows.filter((it) => it.bolge_kategori === activeBolge);
    if (q) rows = rows.filter((it) => it.fiziksel_belirti.toLowerCase().includes(q));
    const sorted = [...rows];
    if (sort === "title-asc") sorted.sort((a, b) => a.fiziksel_belirti.localeCompare(b.fiziksel_belirti, "tr"));
    return sorted;
  }, [items, search, sort, activeBolge]);

  if (sessionQuery.isLoading) {
    return <div className="p-8 text-sm text-slate-500">Yükleniyor...</div>;
  }

  if (!authenticated) {
    return (
      <AdminLoginCard
        configured={configured}
        onLogin={(args) => loginMutation.mutate(args)}
        isLoading={loginMutation.isPending}
        error={loginMutation.isError ? (loginMutation.error as Error).message : undefined}
      />
    );
  }

  return (
    <div className="flex min-h-dvh w-full bg-slate-100/60">
      <SelmaAdminSidebar />

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="border-b border-slate-200 bg-white/80 px-4 py-3 backdrop-blur sm:px-6">
          <div className="flex items-center justify-between gap-3">
            <div className="text-base font-semibold text-slate-900 sm:text-lg">Selma — Rehber İçeriği</div>
            <Button variant="outline" size="sm" className="rounded-full" onClick={() => logoutMutation.mutate()} disabled={logoutMutation.isPending}>
              Çıkış
            </Button>
          </div>
          <div className="mt-3">
            <SelmaAdminMobileNav />
          </div>
        </header>

        <div className="min-w-0 flex-1 p-4 sm:p-6">
          {listQuery.isLoading ? (
            <div className="text-sm text-slate-500">Yükleniyor...</div>
          ) : listQuery.isError ? (
            <div className="text-sm text-destructive">{(listQuery.error as Error).message}</div>
          ) : (
            <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <div className="text-2xl font-extrabold tracking-tight text-slate-900">Rehber İçeriği</div>
                  <div className="mt-1 text-sm text-slate-500">
                    Hastalık/semptom → duygusal neden → olumlama eşleşmeleri (psikosomatik_hastaliklar).
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <a
                    className="flex cursor-pointer items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50"
                    onClick={() => downloadJson("rehber-ornek-format.json", ORNEK_FORMAT)}
                  >
                    Örnek Dosya İndir
                  </a>
                  <Button size="sm" variant="outline" className="rounded-full" onClick={handleExport}>
                    <Download className="mr-1 h-4 w-4" />
                    Dışa Aktar
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="rounded-full"
                    onClick={() => importFileRef.current?.click()}
                    disabled={importMutation.isPending}
                  >
                    <Upload className="mr-1 h-4 w-4" />
                    {importMutation.isPending ? "Yükleniyor…" : "İçe Aktar"}
                  </Button>
                  <input ref={importFileRef} type="file" accept="application/json" className="hidden" onChange={handleImportFile} />
                  <Button size="sm" className="rounded-full" onClick={() => setShowForm(true)}>
                    <Plus className="mr-1 h-4 w-4" />
                    Yeni Ekle
                  </Button>
                </div>
              </div>

              {/* İçe aktarma sonucu özet modalı */}
              {(importResult || importError) && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
                  <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-xl">
                    <div className="mb-3 flex items-center justify-between">
                      <div className="font-semibold text-slate-900">İçe Aktarma Sonucu</div>
                      <button type="button" onClick={() => { setImportResult(null); setImportError(null); }}>
                        <X className="h-4 w-4 text-slate-500" />
                      </button>
                    </div>
                    {importError && (
                      <div className="rounded-xl border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
                        {importError}
                      </div>
                    )}
                    {importResult && (
                      <div className="space-y-3">
                        <div className="flex gap-4 text-sm">
                          <div className="rounded-xl bg-emerald-600/10 px-3 py-2 font-semibold text-emerald-700">
                            {importResult.insertedCount} kayıt eklendi
                          </div>
                          <div className="rounded-xl bg-amber-600/10 px-3 py-2 font-semibold text-amber-700">
                            {importResult.skippedCount} kayıt atlandı
                          </div>
                        </div>
                        {importResult.skipped.length > 0 && (
                          <div className="max-h-60 overflow-y-auto rounded-xl border border-slate-200">
                            <ul className="divide-y divide-slate-100 text-xs">
                              {importResult.skipped.map((s) => (
                                <li key={s.index} className="p-3">
                                  <div className="font-semibold text-slate-700">
                                    Satır {s.index + 1}{s.fiziksel_belirti ? ` — "${s.fiziksel_belirti}"` : ""}
                                  </div>
                                  <div className="mt-0.5 text-slate-500">{s.reason}</div>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}
                    <Button size="sm" className="mt-4 w-full rounded-full" onClick={() => { setImportResult(null); setImportError(null); }}>
                      Kapat
                    </Button>
                  </div>
                </div>
              )}

              {showForm && (
                <form onSubmit={handleSubmit} className="mt-6 rounded-2xl border border-emerald-200 bg-emerald-50/50 p-5">
                  <div className="mb-4 flex items-center justify-between">
                    <div className="font-semibold text-slate-900">Yeni Rehber Kaydı</div>
                    <button type="button" onClick={() => { setShowForm(false); setForm(EMPTY_FORM); }}>
                      <X className="h-4 w-4 text-slate-500" />
                    </button>
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Hastalık İsmi *</label>
                      <input
                        className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-emerald-500/30"
                        value={form.fiziksel_belirti}
                        onChange={(e) => setForm((f) => ({ ...f, fiziksel_belirti: e.target.value }))}
                        placeholder="örn: Migren"
                        required
                      />
                    </div>

                    <div>
                      <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Bölge Kategorisi *</label>
                      <select
                        className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-emerald-500/30"
                        value={form.bolge_kategori}
                        onChange={(e) => setForm((f) => ({ ...f, bolge_kategori: e.target.value as BolgeKategori }))}
                      >
                        {BOLGE_KATEGORILERI.map((b) => (
                          <option key={b.value} value={b.value}>{b.label}</option>
                        ))}
                      </select>
                    </div>

                    <div className="sm:col-span-2">
                      <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Açıklama / Duygusal Neden *</label>
                      <textarea
                        className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-emerald-500/30"
                        rows={2}
                        value={form.duygusal_neden}
                        onChange={(e) => setForm((f) => ({ ...f, duygusal_neden: e.target.value }))}
                        placeholder="Duygusal kök neden açıklaması..."
                        required
                      />
                    </div>

                    <div className="sm:col-span-2">
                      <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Olumlama *</label>
                      <textarea
                        className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-emerald-500/30"
                        rows={2}
                        value={form.olumlama}
                        onChange={(e) => setForm((f) => ({ ...f, olumlama: e.target.value }))}
                        placeholder="Yeni düşünce kalıbı / olumlama cümlesi..."
                        required
                      />
                    </div>

                    <div>
                      <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">İkon (opsiyonel)</label>
                      <input
                        className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-emerald-500/30"
                        value={form.ikon}
                        onChange={(e) => setForm((f) => ({ ...f, ikon: e.target.value }))}
                        placeholder="örn: psychology (Material Symbols adı)"
                      />
                    </div>

                    <div>
                      <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">İlişkili Meditasyon (opsiyonel)</label>
                      <select
                        className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-emerald-500/30"
                        value={form.iliskili_meditasyon_id}
                        onChange={(e) => setForm((f) => ({ ...f, iliskili_meditasyon_id: e.target.value }))}
                      >
                        <option value="">— Seçilmedi —</option>
                        {meditasyonOptions.map((m) => (
                          <option key={m.id} value={m.id}>{m.baslik}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {createMutation.isError && (
                    <div className="mt-3 rounded-xl border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
                      {(createMutation.error as Error).message}
                    </div>
                  )}

                  <div className="mt-4 flex gap-2">
                    <Button type="submit" size="sm" className="rounded-full" disabled={createMutation.isPending}>
                      {createMutation.isPending ? "Kaydediliyor…" : "Kaydet"}
                    </Button>
                    <Button type="button" size="sm" variant="outline" className="rounded-full" onClick={() => { setShowForm(false); setForm(EMPTY_FORM); }}>
                      İptal
                    </Button>
                  </div>
                </form>
              )}

              <div className="mt-6 flex flex-col gap-4 lg:flex-row lg:items-center">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <input
                    className="h-11 w-full rounded-2xl bg-slate-100 pl-10 pr-4 text-sm text-slate-900 outline-none ring-emerald-600/20 focus:ring-4"
                    placeholder="Hastalık ismi ara..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <div className="flex items-center gap-2">
                    <div className="text-xs font-medium text-slate-500">Sırala:</div>
                    <select
                      className="h-10 rounded-full border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 outline-none"
                      value={sort}
                      onChange={(e) => setSort(e.target.value as any)}
                    >
                      <option value="newest">En Yeni</option>
                      <option value="title-asc">Hastalık İsmi (A-Z)</option>
                    </select>
                  </div>
                  <div className="flex items-center gap-1 rounded-2xl border border-slate-200 bg-white p-1">
                    <button
                      type="button"
                      className={cn("grid h-8 w-8 place-items-center rounded-xl transition-colors", viewMode === "grid" ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-900")}
                      onClick={() => setViewMode("grid")}
                      title="Grid görünümü"
                    >
                      <LayoutGrid className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      className={cn("grid h-8 w-8 place-items-center rounded-xl transition-colors", viewMode === "list" ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-900")}
                      onClick={() => setViewMode("list")}
                      title="Liste görünümü"
                    >
                      <List className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Bölge kategorisi filtre chip'leri */}
              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setActiveBolge("tumu")}
                  className={cn(
                    "rounded-full px-3 py-1.5 text-xs font-semibold transition-colors",
                    activeBolge === "tumu" ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200",
                  )}
                >
                  Tümü
                </button>
                {BOLGE_KATEGORILERI.map((b) => (
                  <button
                    key={b.value}
                    type="button"
                    onClick={() => setActiveBolge(b.value)}
                    className={cn(
                      "rounded-full px-3 py-1.5 text-xs font-semibold transition-colors",
                      activeBolge === b.value ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200",
                    )}
                  >
                    {b.label}
                  </button>
                ))}
              </div>

              {deleteMutation.isError && (
                <div className="mt-4 rounded-2xl border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
                  {(deleteMutation.error as Error).message}
                </div>
              )}

              {editingItem && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      updateMutation.mutate({
                        id: editingItem.id,
                        fiziksel_belirti: editingItem.fiziksel_belirti,
                        bolge_kategori: editingItem.bolge_kategori,
                        duygusal_neden: editingItem.duygusal_neden,
                        olumlama: editingItem.olumlama,
                        ikon: editingItem.ikon,
                        iliskili_meditasyon_id: editingItem.iliskili_meditasyon_id,
                      });
                    }}
                    className="flex max-h-[90vh] w-full max-w-lg flex-col rounded-2xl border border-slate-200 bg-white shadow-xl"
                  >
                    <div className="flex flex-shrink-0 items-center justify-between rounded-t-2xl border-b border-slate-200 bg-white p-6 pb-4">
                      <div className="font-semibold text-slate-900">Rehber Kaydını Düzenle</div>
                      <button type="button" onClick={() => setEditingItem(null)}>
                        <X className="h-4 w-4 text-slate-500" />
                      </button>
                    </div>
                    <div className="overflow-y-auto p-6">
                      <div className="grid gap-4">
                        <div>
                          <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Hastalık İsmi *</label>
                          <input
                            className="h-10 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-emerald-500/30"
                            value={editingItem.fiziksel_belirti}
                            onChange={(e) => setEditingItem((p) => p && { ...p, fiziksel_belirti: e.target.value })}
                            required
                          />
                        </div>
                        <div>
                          <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Bölge Kategorisi *</label>
                          <select
                            className="h-10 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-emerald-500/30"
                            value={editingItem.bolge_kategori}
                            onChange={(e) => setEditingItem((p) => p && { ...p, bolge_kategori: e.target.value as BolgeKategori })}
                          >
                            {BOLGE_KATEGORILERI.map((b) => (
                              <option key={b.value} value={b.value}>{b.label}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Açıklama / Duygusal Neden *</label>
                          <textarea
                            className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-emerald-500/30"
                            rows={2}
                            value={editingItem.duygusal_neden}
                            onChange={(e) => setEditingItem((p) => p && { ...p, duygusal_neden: e.target.value })}
                            required
                          />
                        </div>
                        <div>
                          <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Olumlama *</label>
                          <textarea
                            className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-emerald-500/30"
                            rows={2}
                            value={editingItem.olumlama}
                            onChange={(e) => setEditingItem((p) => p && { ...p, olumlama: e.target.value })}
                            required
                          />
                        </div>
                        <div>
                          <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">İkon (opsiyonel)</label>
                          <input
                            className="h-10 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-emerald-500/30"
                            value={editingItem.ikon ?? ""}
                            onChange={(e) => setEditingItem((p) => p && { ...p, ikon: e.target.value || null })}
                          />
                        </div>
                        <div>
                          <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">İlişkili Meditasyon (opsiyonel)</label>
                          <select
                            className="h-10 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-emerald-500/30"
                            value={editingItem.iliskili_meditasyon_id ?? ""}
                            onChange={(e) => setEditingItem((p) => p && { ...p, iliskili_meditasyon_id: e.target.value || null })}
                          >
                            <option value="">— Seçilmedi —</option>
                            {meditasyonOptions.map((m) => (
                              <option key={m.id} value={m.id}>{m.baslik}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      {updateMutation.isError && (
                        <div className="mt-3 rounded-xl border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
                          {(updateMutation.error as Error).message}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-shrink-0 gap-2 rounded-b-2xl border-t border-slate-200 bg-white p-6 pt-4">
                      <Button type="submit" size="sm" className="rounded-full" disabled={updateMutation.isPending}>
                        {updateMutation.isPending ? "Kaydediliyor…" : "Kaydet"}
                      </Button>
                      <Button type="button" size="sm" variant="outline" className="rounded-full" onClick={() => setEditingItem(null)}>
                        İptal
                      </Button>
                    </div>
                  </form>
                </div>
              )}

              {viewMode === "grid" ? (
                <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {visibleItems.map((item) => (
                    <div key={item.id} className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                      <div className="flex items-start justify-between gap-2">
                        <div className="font-bold text-slate-900">{item.fiziksel_belirti}</div>
                        <div className="flex flex-shrink-0 items-center gap-1">
                          <button type="button" className="rounded-xl p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700" onClick={() => setEditingItem(item)}>
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            className="rounded-xl p-1.5 text-rose-500 hover:bg-rose-50"
                            disabled={deleteMutation.isPending}
                            onClick={() => {
                              if (confirm(`"${item.fiziksel_belirti}" silinsin mi?`)) deleteMutation.mutate(item.id);
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                      <span className="mt-2 inline-block rounded-full bg-emerald-600/10 px-2 py-0.5 text-[11px] font-bold text-emerald-700">
                        {bolgeLabel(item.bolge_kategori)}
                      </span>
                      <div className="mt-2 line-clamp-2 text-sm text-slate-500">{item.duygusal_neden}</div>
                    </div>
                  ))}
                  {items.length === 0 && !showForm && (
                    <button type="button" onClick={() => setShowForm(true)} className="flex flex-col items-center justify-center rounded-2xl border-2 border-dashed border-slate-200 bg-white p-8 text-slate-500 hover:border-emerald-400/50 hover:bg-emerald-50/50">
                      <Plus className="mb-2 h-8 w-8" />
                      <div className="font-bold">İlk rehber kaydını ekle</div>
                    </button>
                  )}
                </div>
              ) : (
                <div className="mt-6 flex flex-col gap-2">
                  {visibleItems.map((item) => (
                    <div key={item.id} className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="truncate text-sm font-bold text-slate-900">{item.fiziksel_belirti}</span>
                          <span className="flex-none rounded-full bg-emerald-600/10 px-2 py-0.5 text-[10px] font-bold text-emerald-700">
                            {bolgeLabel(item.bolge_kategori)}
                          </span>
                        </div>
                        <div className="mt-0.5 truncate text-xs text-slate-500">{item.duygusal_neden}</div>
                      </div>
                      <div className="flex flex-none items-center gap-1">
                        <button type="button" className="grid h-8 w-8 place-items-center rounded-xl bg-slate-100 text-slate-700 hover:bg-slate-200" onClick={() => setEditingItem(item)}>
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        <button
                          type="button"
                          className="grid h-8 w-8 place-items-center rounded-xl bg-slate-100 text-rose-600 hover:bg-rose-50"
                          disabled={deleteMutation.isPending}
                          onClick={() => {
                            if (confirm(`"${item.fiziksel_belirti}" silinsin mi?`)) deleteMutation.mutate(item.id);
                          }}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                  {items.length === 0 && !showForm && (
                    <button type="button" onClick={() => setShowForm(true)} className="flex items-center gap-4 rounded-2xl border-2 border-dashed border-slate-200 bg-white px-6 py-4 text-slate-600 transition-colors hover:border-emerald-500/50 hover:bg-emerald-50/50">
                      <div className="grid h-10 w-10 flex-none place-items-center rounded-xl bg-slate-100 text-slate-900">
                        <span className="text-xl">+</span>
                      </div>
                      <div className="text-sm font-bold">İlk rehber kaydını ekle</div>
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
